/**
 * IndexedDB cache service for card data
 *
 * Provides two separate caches:
 * 1. Card Index Cache - Minimal card data for fast import matching (~30MB)
 * 2. Full Card Cache - Complete ScryfallCard data for collection cards
 *
 * Uses a singleton pattern to ensure database upgrades complete before any operations.
 */
import type { ScryfallCard } from '@/types/card';

const DB_NAME = 'mtgcraft-cache';
const DB_VERSION = 2; // Bumped for new stores
const STORE_NAME = 'cards'; // Card index store
const META_STORE = 'metadata';
const FULL_CARD_STORE = 'fullCards'; // Full Scryfall card cache
const FULL_CARD_META_KEY = 'fullCardCache';
// Bump when card docs gain fields so returning users refetch (3: `finishes`)
const FULL_CARD_CACHE_SCHEMA_VERSION = 3;

interface CacheMetadata {
  version: number;
  cardCount: number;
  updatedAt: string;
}

interface FullCardCacheMetadata {
  schemaVersion: number;
  cardCount: number;
  updatedAt: string;
}

// Singleton database connection promise to prevent race conditions during upgrade
let dbPromise: Promise<IDBDatabase> | null = null;

/**
 * Open the IndexedDB database (singleton pattern)
 * Ensures only one upgrade happens even with concurrent calls.
 * The connection is kept open for reuse.
 */
function openDatabase(): Promise<IDBDatabase> {
  if (dbPromise) {
    return dbPromise;
  }

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      dbPromise = null; // Reset on error to allow retry
      reject(request.error);
    };

    request.onsuccess = () => {
      const db = request.result;

      // Handle connection closing unexpectedly
      db.onclose = () => {
        dbPromise = null;
      };

      resolve(db);
    };

    request.onblocked = () => {
      dbPromise = null;
      console.warn('[IndexedDB] Database upgrade blocked - close other tabs and reload');
      reject(new Error('Database upgrade blocked'));
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create cards store with id as key (for card index)
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }

      // Create metadata store
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE);
      }

      // Create full cards store (for Scryfall card cache)
      if (!db.objectStoreNames.contains(FULL_CARD_STORE)) {
        db.createObjectStore(FULL_CARD_STORE, { keyPath: 'id' });
      }
    };
  });

  return dbPromise;
}

/**
 * Get cached metadata (version info)
 */
export async function getCacheMetadata(): Promise<CacheMetadata | null> {
  try {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(META_STORE, 'readonly');
      const store = transaction.objectStore(META_STORE);
      const request = store.get('cardIndex');

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result ?? null);
    });
  } catch (error) {
    console.warn('Failed to get cache metadata:', error);
    return null;
  }
}

/**
 * Save metadata to cache
 */
export async function setCacheMetadata(metadata: CacheMetadata): Promise<void> {
  try {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(META_STORE, 'readwrite');
      const store = transaction.objectStore(META_STORE);
      store.put(metadata, 'cardIndex');

      transaction.onerror = () => reject(transaction.error);
      transaction.oncomplete = () => resolve();
    });
  } catch (error) {
    console.warn('Failed to set cache metadata:', error);
  }
}

/**
 * Get all cached cards
 */
export async function getCachedCards<T>(): Promise<T[]> {
  try {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result as T[]);
    });
  } catch (error) {
    console.warn('Failed to get cached cards:', error);
    return [];
  }
}

/**
 * Save cards to cache (bulk insert)
 */
export async function setCachedCards<T extends { id: string }>(cards: T[]): Promise<void> {
  try {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      // Clear existing data first
      store.clear();

      // Insert all cards
      for (const card of cards) {
        store.put(card);
      }

      transaction.onerror = () => reject(transaction.error);
      transaction.oncomplete = () => resolve();
    });
  } catch (error) {
    console.warn('Failed to set cached cards:', error);
    throw error;
  }
}

/**
 * Clear all cached data
 */
export async function clearCache(): Promise<void> {
  try {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME, META_STORE], 'readwrite');

      transaction.objectStore(STORE_NAME).clear();
      transaction.objectStore(META_STORE).clear();

      transaction.onerror = () => reject(transaction.error);
      transaction.oncomplete = () => resolve();
    });
  } catch (error) {
    console.warn('Failed to clear cache:', error);
  }
}

/**
 * Check if IndexedDB is supported
 */
export function isIndexedDBSupported(): boolean {
  return typeof indexedDB !== 'undefined';
}

// ============================================================================
// Full Card Cache (Scryfall data for collection cards)
// ============================================================================

/**
 * Get all cached full cards as a Map
 */
export async function getFullCardCache(): Promise<Map<string, ScryfallCard>> {
  try {
    const db = await openDatabase();
    const metadata = await new Promise<FullCardCacheMetadata | undefined>((resolve, reject) => {
      const transaction = db.transaction(META_STORE, 'readonly');
      const request = transaction.objectStore(META_STORE).get(FULL_CARD_META_KEY);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
    if (metadata?.schemaVersion !== FULL_CARD_CACHE_SCHEMA_VERSION) {
      await clearFullCardCache();
      return new Map();
    }

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(FULL_CARD_STORE, 'readonly');
      const store = transaction.objectStore(FULL_CARD_STORE);
      const request = store.getAll();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const cards = request.result as ScryfallCard[];
        const map = new Map<string, ScryfallCard>();
        for (const card of cards) {
          map.set(card.id, card);
        }
        resolve(map);
      };
    });
  } catch (error) {
    console.warn('Failed to get full card cache:', error);
    return new Map();
  }
}

/**
 * Add cards to the full card cache (merge, don't replace)
 * Also updates metadata with current timestamp and count
 */
export async function addToFullCardCache(cards: ScryfallCard[]): Promise<void> {
  if (cards.length === 0) return;

  try {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([FULL_CARD_STORE, META_STORE], 'readwrite');
      const cardStore = transaction.objectStore(FULL_CARD_STORE);
      const metaStore = transaction.objectStore(META_STORE);

      // Add/update cards
      for (const card of cards) {
        cardStore.put(card);
      }

      // Get current count for metadata update
      const countRequest = cardStore.count();
      countRequest.onsuccess = () => {
        const metadata: FullCardCacheMetadata = {
          schemaVersion: FULL_CARD_CACHE_SCHEMA_VERSION,
          cardCount: countRequest.result,
          updatedAt: new Date().toISOString(),
        };
        metaStore.put(metadata, FULL_CARD_META_KEY);
      };

      transaction.onerror = () => reject(transaction.error);
      transaction.oncomplete = () => resolve();
    });
  } catch (error) {
    console.warn('Failed to add to full card cache:', error);
  }
}

/**
 * Clear the full card cache
 */
export async function clearFullCardCache(): Promise<void> {
  try {
    const db = await openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([FULL_CARD_STORE, META_STORE], 'readwrite');

      transaction.objectStore(FULL_CARD_STORE).clear();
      transaction.objectStore(META_STORE).delete(FULL_CARD_META_KEY);

      transaction.onerror = () => reject(transaction.error);
      transaction.oncomplete = () => resolve();
    });
  } catch (error) {
    console.warn('Failed to clear full card cache:', error);
  }
}
